import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  ChevronLeft, Save, Receipt, Building2, Package, PackageCheck, FileText, IndianRupee,
  Calendar, ShieldCheck, AlertTriangle, CheckCircle2, Wallet, Info, Clock
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const h = React.createElement;

const fmtINR = (n) => "\u20B9" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "\u2014";

function PaymentRecommendationForm() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [searchParams] = useSearchParams();
  const initialPOId = searchParams.get("po") || "";

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [advices, setAdvices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    po_id: initialPOId,
    item_name: "",
    product_serial_number: "",
    hsn_code: "",
    // GRN details — captured here for Payment Advice context
    grn_number: "",
    grn_date: "",
    grn_category: "",
    grn_subcategory: "",
    grn_classification: "",
    date_received: "",
    installation_date: "",
    equipment_status: "SATISFACTORY",
    warranty_received: false,
    delay_in_delivery: false,
    ld_applicable: false,
    ld_waived: false,
    delay_justification: "",
    payment_type: "FULL",
    part_payment_percentage: "",
    invoice_number: "",
    invoice_date: "",
    invoice_amount: "",
    remarks: ""
  });

  // Toggle helpers so LD Charges Applicable and LD Waiver Recommended are MUTUALLY EXCLUSIVE
  const toggleLdApplicable = (checked) => setForm(f => ({ ...f, ld_applicable: checked, ld_waived: checked ? false : f.ld_waived }));
  const toggleLdWaiver = (checked) => setForm(f => ({ ...f, ld_waived: checked, ld_applicable: checked ? false : f.ld_applicable }));

  // GRN dropdown options — Categories & Subcategories load dynamically from
  // the institutional "Asset Category" master (GET /api/categories)
  const [grnCategories, setGrnCategories] = useState([]); // [{name, code, subcategories:[...]}]
  const subcatsForSelected = useMemo(() => {
    const row = grnCategories.find(c => c.name === form.grn_category);
    return row ? (row.subcategories || []) : [];
  }, [grnCategories, form.grn_category]);
  const GRN_CLASSIFICATIONS = [
    "Capital Expenditure", "Revenue Expenditure", "Project Equipment", "Institutional Asset", "Short-life Consumable"
  ];

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [pos, pays, cats] = await Promise.all([
          axios.get(`${API_URL}/purchase-orders`, getAuthHeader()),
          axios.get(`${API_URL}/payment-recommendations`, getAuthHeader()),
          axios.get(`${API_URL}/categories`, getAuthHeader()),
        ]);
        setPurchaseOrders(pos.data || []);
        setAdvices(pays.data || []);
        setGrnCategories((cats.data && cats.data.categories) || []);
      } catch { toast.error("Failed to load Purchase Orders"); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedPO = useMemo(() => purchaseOrders.find(p => p.id === form.po_id), [purchaseOrders, form.po_id]);
  const poValue = selectedPO?.po_value || selectedPO?.total_amount || 0;
  const poItems = selectedPO?.items || [];

  // Past payment advice for this PO
  const priorAdvices = advices.filter(a => a.po_id === form.po_id);
  const priorPaid = priorAdvices.filter(a => a.status === "APPROVED").reduce((s, a) => s + (a.invoice_amount || 0), 0);
  const priorPending = priorAdvices.filter(a => a.status === "PENDING").reduce((s, a) => s + (a.invoice_amount || 0), 0);
  const balance = Math.max(0, poValue - priorPaid - priorPending);

  // Auto-prefill item name when PO selected (first item by default)
  useEffect(() => {
    if (selectedPO && poItems.length > 0 && !form.item_name) {
      setForm(f => ({
        ...f,
        item_name: poItems[0]?.description || poItems[0]?.item_name || selectedPO.subject || "",
        hsn_code: poItems[0]?.hsn_code || ""
      }));
    }
  }, [selectedPO?.id]);

  // Compute suggested invoice amount
  const suggestedAmount = useMemo(() => {
    if (form.payment_type === "FULL") return balance > 0 ? balance : poValue;
    const pct = parseFloat(form.part_payment_percentage) || 0;
    return Math.round((poValue * pct) / 100);
  }, [form.payment_type, form.part_payment_percentage, poValue, balance]);

  // Compute days late if PO has delivery_weeks/months and date_received exists
  const deliveryAnalysis = useMemo(() => {
    if (!selectedPO || !form.date_received) return null;
    const poDate = selectedPO.po_date ? new Date(selectedPO.po_date) : null;
    if (!poDate) return null;
    const weeks = selectedPO.delivery_weeks || 0;
    const months = selectedPO.delivery_months || 0;
    if (!weeks && !months) return null;
    const expectedDate = new Date(poDate);
    if (weeks) expectedDate.setDate(expectedDate.getDate() + weeks * 7);
    if (months) expectedDate.setMonth(expectedDate.getMonth() + months);
    const recvDate = new Date(form.date_received);
    const diffMs = recvDate - expectedDate;
    const daysLate = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return { expectedDate, daysLate, isLate: daysLate > 0 };
  }, [selectedPO, form.date_received]);

  // Auto-flag delay
  useEffect(() => {
    if (deliveryAnalysis?.isLate && !form.delay_in_delivery) {
      setForm(f => ({ ...f, delay_in_delivery: true, ld_applicable: true }));
    }
  }, [deliveryAnalysis?.isLate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.po_id) return toast.error("Please select a Purchase Order");
    if (!form.item_name) return toast.error("Item name is required");
    if (!form.invoice_number) return toast.error("Invoice number is required");
    if (!form.invoice_date) return toast.error("Invoice date is required");
    if (!form.date_received) return toast.error("Date received is required");
    if (!form.grn_category) return toast.error("GRN Category is required \u2014 classify the goods before submitting");
    if (!form.grn_subcategory) return toast.error("GRN Subcategory is required");
    const amt = parseFloat(form.invoice_amount);
    if (!amt || amt <= 0) return toast.error("Invoice amount must be greater than zero");
    if (form.payment_type === "PART") {
      const pct = parseFloat(form.part_payment_percentage);
      if (!pct || pct <= 0 || pct >= 100) return toast.error("Part payment % must be between 1 and 99");
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/payment-recommendations`, {
        ...form,
        invoice_amount: amt,
        part_payment_percentage: form.payment_type === "PART" ? parseFloat(form.part_payment_percentage) : null
      }, getAuthHeader());
      toast.success("Payment Advice submitted successfully");
      navigate("/payments");
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return h("div", { className: "p-12 text-center text-slate-400" }, "Loading\u2026");

  // Helper: section header
  const sectionHead = (Icon, title, desc) => h("div", { className: "flex items-start gap-3 mb-4 pb-3 border-b border-slate-100" },
    h("div", { className: "p-2 rounded-lg bg-slate-900 text-white" }, h(Icon, { className: "w-4 h-4" })),
    h("div", null,
      h("h3", { className: "text-sm font-semibold text-slate-900" }, title),
      desc && h("p", { className: "text-xs text-slate-500 mt-0.5" }, desc)
    )
  );

  // PO Summary card
  const renderPOSummary = () => {
    if (!selectedPO) {
      return h("div", { className: "p-8 text-center bg-slate-50/50 border border-dashed border-slate-200 rounded-xl" },
        h(ShoppingCartIcon, null),
        h("p", { className: "text-sm text-slate-500 mt-2" }, "Select a Purchase Order to begin")
      );
    }
    return h("div", { className: "rounded-xl border border-slate-200 overflow-hidden bg-gradient-to-br from-slate-50 to-white" },
      // Header strip
      h("div", { className: "px-5 py-3 bg-slate-900 text-white flex items-center justify-between" },
        h("div", null,
          h("p", { className: "text-xs uppercase tracking-wider text-slate-300 font-semibold" }, "Purchase Order"),
          h("p", { className: "text-lg font-bold font-mono" }, selectedPO.po_number || "—")
        ),
        h("div", { className: "text-right" },
          h("p", { className: "text-xs uppercase tracking-wider text-slate-300" }, "PO Value"),
          h("p", { className: "text-2xl font-bold text-emerald-400" }, fmtINR(poValue))
        )
      ),
      // Body grid
      h("div", { className: "p-5 grid grid-cols-1 md:grid-cols-3 gap-5 text-sm" },
        // Vendor
        h("div", null,
          h("p", { className: "text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5" }, h(Building2, { className: "w-3 h-3" }), "Vendor"),
          h("p", { className: "font-semibold text-slate-900" }, selectedPO.vendor_details?.name || "—"),
          selectedPO.vendor_details?.gst_number && h("p", { className: "text-xs text-slate-500 font-mono" }, "GST: " + selectedPO.vendor_details.gst_number),
          selectedPO.vendor_details?.contact_person && h("p", { className: "text-xs text-slate-500" }, selectedPO.vendor_details.contact_person)
        ),
        // Type & date
        h("div", null,
          h("p", { className: "text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5" }, h(FileText, { className: "w-3 h-3" }), "Order"),
          h("div", { className: "text-slate-700" }, h(Badge, { variant: "outline", className: "text-xs" }, (selectedPO.po_type || "DOMESTIC") + " / " + (selectedPO.po_sub_type || "GENERAL"))),
          h("p", { className: "text-xs text-slate-500 mt-1" }, "Dated: ", fmtDate(selectedPO.po_date || selectedPO.created_at)),
          selectedPO.delivery_weeks && h("p", { className: "text-xs text-slate-500" }, "Delivery: ", selectedPO.delivery_weeks, " weeks")
        ),
        // Status
        h("div", null,
          h("p", { className: "text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5" }, h(Receipt, { className: "w-3 h-3" }), "Payment Status"),
          h("div", { className: "flex items-baseline gap-2" },
            h("p", { className: "text-lg font-bold text-emerald-700" }, fmtINR(priorPaid)),
            h("p", { className: "text-xs text-slate-400" }, "paid")
          ),
          priorPending > 0 && h("p", { className: "text-xs text-amber-600" }, fmtINR(priorPending), " pending"),
          h("p", { className: "text-xs font-semibold text-slate-700 mt-0.5" }, "Balance: ", h("span", { className: "text-slate-900" }, fmtINR(balance)))
        )
      ),
      // Items list
      poItems.length > 0 && h("div", { className: "px-5 pb-5" },
        h("p", { className: "text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5" }, h(Package, { className: "w-3 h-3" }), "Items in PO (", poItems.length, ")"),
        h("div", { className: "space-y-1.5 max-h-32 overflow-y-auto" },
          poItems.slice(0, 5).map((it, idx) => h("div", { key: idx, className: "flex items-center justify-between p-2 bg-white rounded border border-slate-100 text-xs" },
            h("span", { className: "truncate flex-1 text-slate-700" }, (idx + 1) + ". " + (it.description || it.item_name || "Item")),
            h("span", { className: "text-slate-500 ml-2 whitespace-nowrap" }, (it.quantity || 1) + " " + (it.unit || "Nos"), " \u00D7 ", fmtINR(it.unit_price || it.estimated_cost || 0))
          )),
          poItems.length > 5 && h("p", { className: "text-xs text-slate-400 italic text-center" }, "+ ", poItems.length - 5, " more items")
        )
      ),
      // Prior advices badge
      priorAdvices.length > 0 && h("div", { className: "px-5 pb-4" },
        h("div", { className: "flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800" },
          h(Info, { className: "w-3.5 h-3.5" }),
          priorAdvices.length, " prior payment advice", priorAdvices.length > 1 ? "s" : "", " on this PO \u2014 ",
          priorAdvices.filter(a => a.status === "APPROVED").length, " approved, ",
          priorAdvices.filter(a => a.status === "PENDING").length, " pending"
        )
      )
    );
  };

  // Empty state icon helper
  const ShoppingCartIcon = () => h("svg", { xmlns: "http://www.w3.org/2000/svg", className: "w-12 h-12 text-slate-300 mx-auto", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5" }, h("path", { d: "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" }));

  return h("div", { className: "max-w-5xl mx-auto space-y-6", "data-testid": "payment-advice-form-page" },
    // Header
    h("div", { className: "flex items-center gap-4" },
      h(Button, { variant: "outline", size: "sm", onClick: () => navigate("/payments"), "data-testid": "back-btn" },
        h(ChevronLeft, { className: "w-4 h-4 mr-1" }), "Back"
      ),
      h("div", null,
        h("h1", { className: "text-xl font-bold text-slate-900" }, "Create Payment Advice"),
        h("p", { className: "text-xs text-slate-500" }, "Recommend payment release for delivered items \u2014 partial or full")
      )
    ),

    // PO Selector + Summary
    h(Card, { className: "border-slate-200" }, h(CardContent, { className: "p-5" },
      h(Label, { className: "text-xs font-semibold text-slate-700 uppercase tracking-wider" }, "Step 1 \u2014 Select Purchase Order *"),
      h("select", {
        value: form.po_id,
        onChange: e => set("po_id", e.target.value),
        className: "w-full mt-2 mb-4 h-11 px-3 rounded-md border border-slate-300 text-sm bg-white",
        "data-testid": "po-select"
      },
        h("option", { value: "" }, "-- Choose a PO --"),
        purchaseOrders.map(po => h("option", { key: po.id, value: po.id },
          (po.po_number || "PO") + " \u2014 " + (po.vendor_details?.name || "Unknown") +
          " \u2014 " + fmtINR(po.po_value || po.total_amount || 0) +
          " (" + (po.status || "DRAFT") + ")"
        ))
      ),
      renderPOSummary()
    )),

    // Form sections
    selectedPO && h("form", { onSubmit: submit, className: "space-y-6" },

      // Section: Receipt Details
      h(Card, { className: "border-slate-200" }, h(CardContent, { className: "p-5" },
        sectionHead(Package, "Receipt & Installation", "When and how the items were received"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Item Description *"),
            h(Input, { value: form.item_name, onChange: e => set("item_name", e.target.value), placeholder: "What was received", className: "mt-1.5", "data-testid": "item-name-input" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "HSN Code"),
            h(Input, { value: form.hsn_code, onChange: e => set("hsn_code", e.target.value), className: "mt-1.5" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Product Serial Number"),
            h(Input, { value: form.product_serial_number, onChange: e => set("product_serial_number", e.target.value), placeholder: "Serial / Asset tag", className: "mt-1.5" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Equipment Status"),
            h("select", { value: form.equipment_status, onChange: e => set("equipment_status", e.target.value), className: "w-full h-9 px-3 mt-1.5 rounded-md border border-slate-200 text-sm bg-white" },
              h("option", { value: "SATISFACTORY" }, "Satisfactory \u2014 working as expected"),
              h("option", { value: "UNSATISFACTORY" }, "Unsatisfactory \u2014 issues found")
            )
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Date Received *"),
            h(Input, { type: "date", value: form.date_received, onChange: e => set("date_received", e.target.value), className: "mt-1.5", "data-testid": "date-received-input" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Installation Date"),
            h(Input, { type: "date", value: form.installation_date, onChange: e => set("installation_date", e.target.value), className: "mt-1.5" })
          )
        ),
        h("div", { className: "flex items-center gap-3 mt-4 p-3 bg-slate-50 rounded-md border border-slate-100" },
          h("input", { type: "checkbox", id: "warranty", checked: form.warranty_received, onChange: e => set("warranty_received", e.target.checked), className: "rounded" }),
          h("label", { htmlFor: "warranty", className: "flex items-center gap-2 text-sm text-slate-700 cursor-pointer" },
            h(ShieldCheck, { className: "w-4 h-4 text-emerald-600" }),
            "Warranty / Guarantee certificate received"
          )
        ),
        // Auto delivery analysis
        deliveryAnalysis && h("div", {
          className: "mt-4 p-3 rounded-md border " + (deliveryAnalysis.isLate ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200")
        },
          h("div", { className: "flex items-start gap-2" },
            h(Calendar, { className: "w-4 h-4 mt-0.5 " + (deliveryAnalysis.isLate ? "text-amber-700" : "text-emerald-700") }),
            h("div", { className: "flex-1 text-xs" },
              h("p", { className: "font-semibold " + (deliveryAnalysis.isLate ? "text-amber-900" : "text-emerald-900") },
                deliveryAnalysis.isLate ? `Delayed by ${deliveryAnalysis.daysLate} day${deliveryAnalysis.daysLate > 1 ? "s" : ""}` : "On-time delivery"),
              h("p", { className: "text-slate-600 mt-0.5" }, "Expected: ", fmtDate(deliveryAnalysis.expectedDate), " \u2022 Received: ", fmtDate(form.date_received))
            )
          )
        )
      )),

      // Section: GRN Details — classification context required before releasing payment
      h(Card, { className: "border-slate-200", "data-testid": "grn-details-section" }, h(CardContent, { className: "p-5" },
        sectionHead(PackageCheck, "GRN Details", "Classify the goods received — required before proceeding to payment"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "GRN Number"),
            h(Input, { value: form.grn_number, onChange: e => set("grn_number", e.target.value), placeholder: "e.g. GRN/2026/00012", className: "mt-1.5", "data-testid": "grn-number-input" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "GRN Date"),
            h(Input, { type: "date", value: form.grn_date, onChange: e => set("grn_date", e.target.value), className: "mt-1.5", "data-testid": "grn-date-input" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Category *"),
            h("select", {
              value: form.grn_category,
              onChange: e => { set("grn_category", e.target.value); set("grn_subcategory", ""); },
              className: "w-full h-9 px-3 mt-1.5 rounded-md border border-slate-300 text-sm bg-white",
              "data-testid": "grn-category-select"
            },
              h("option", { value: "" }, "Select category..."),
              ...grnCategories.map(c => h("option", { key: c.name, value: c.name }, c.name))
            )
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Subcategory *"),
            h("select", {
              value: form.grn_subcategory,
              onChange: e => set("grn_subcategory", e.target.value),
              disabled: !form.grn_category,
              className: "w-full h-9 px-3 mt-1.5 rounded-md border border-slate-300 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400",
              "data-testid": "grn-subcategory-select"
            },
              h("option", { value: "" }, form.grn_category ? "Select subcategory..." : "\u2014 Pick category first \u2014"),
              ...subcatsForSelected.map(s => h("option", { key: s, value: s }, s))
            )
          ),
          h("div", { className: "md:col-span-2" },
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Expenditure Classification"),
            h("select", {
              value: form.grn_classification,
              onChange: e => set("grn_classification", e.target.value),
              className: "w-full h-9 px-3 mt-1.5 rounded-md border border-slate-300 text-sm bg-white",
              "data-testid": "grn-classification-select"
            },
              h("option", { value: "" }, "Select classification..."),
              ...GRN_CLASSIFICATIONS.map(c => h("option", { key: c, value: c }, c))
            )
          )
        ),
        // Hint banner
        h("div", { className: "mt-4 flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200" },
          h(Info, { className: "w-4 h-4 text-blue-700 mt-0.5 shrink-0" }),
          h("p", { className: "text-xs text-blue-900" },
            "Category and subcategory help F&A route this payment correctly. ",
            h("strong", null, "Required"),
            " to proceed."
          )
        )
      )),
      h(Card, { className: "border-slate-200" }, h(CardContent, { className: "p-5" },
        sectionHead(AlertTriangle, "Delay & Liquidated Damages", "Mark delays and LD applicability per PO terms"),
        // Top-level: Delay in Delivery
        h("label", { className: "flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer w-full md:w-1/3" },
          h("input", { type: "checkbox", checked: form.delay_in_delivery, onChange: e => set("delay_in_delivery", e.target.checked), className: "rounded", "data-testid": "ld-delay-cb" }),
          h("span", { className: "text-sm font-medium text-slate-700" }, "Delay in Delivery")
        ),
        // Conditional: when there IS a delay, show BOTH LD options together (mutually exclusive)
        form.delay_in_delivery && h("div", { className: "mt-3 space-y-3" },
          h("p", { className: "text-xs text-slate-500 italic" }, "Select ONE option below \u2014 LD Charges and LD Waiver are mutually exclusive."),
          h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-3" },
            h("label", { className: "flex items-center gap-3 p-3 rounded-md border " + (form.ld_applicable ? "border-amber-400 bg-amber-50 ring-2 ring-amber-200" : "border-amber-200 bg-amber-50/30 hover:bg-amber-50") + " cursor-pointer transition-all" },
              h("input", { type: "checkbox", checked: form.ld_applicable, onChange: e => toggleLdApplicable(e.target.checked), className: "rounded", "data-testid": "ld-applicable-cb" }),
              h("span", { className: "text-sm font-medium text-amber-900" }, "LD Charges Applicable")
            ),
            h("label", { className: "flex items-center gap-3 p-3 rounded-md border " + (form.ld_waived ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200" : "border-emerald-200 bg-emerald-50/30 hover:bg-emerald-50") + " cursor-pointer transition-all" },
              h("input", { type: "checkbox", checked: form.ld_waived, onChange: e => toggleLdWaiver(e.target.checked), className: "rounded", "data-testid": "ld-waiver-cb" }),
              h("span", { className: "text-sm font-medium text-emerald-900" }, "LD Waiver Recommended")
            )
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Justification (delay / waiver / LD calc basis)"),
            h(Textarea, { value: form.delay_justification, onChange: e => set("delay_justification", e.target.value), rows: 2, className: "mt-1.5", placeholder: "Reason behind delay; supplier explanation; rationale for LD waiver; impact on project; LD calculation basis..." })
          )
        )
      )),

      // Section: Invoice
      h(Card, { className: "border-slate-200" }, h(CardContent, { className: "p-5" },
        sectionHead(FileText, "Invoice Details", "Vendor invoice that this advice will release payment for"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4" },
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Invoice Number *"),
            h(Input, { value: form.invoice_number, onChange: e => set("invoice_number", e.target.value), placeholder: "e.g. INV/2025/00123", className: "mt-1.5", "data-testid": "invoice-number-input" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Invoice Date *"),
            h(Input, { type: "date", value: form.invoice_date, onChange: e => set("invoice_date", e.target.value), className: "mt-1.5" })
          ),
          h("div", null,
            h(Label, { className: "text-xs font-medium text-slate-600" }, "Invoice Amount (\u20B9) *"),
            h(Input, { type: "number", min: 0, step: "0.01", value: form.invoice_amount, onChange: e => set("invoice_amount", e.target.value), placeholder: "0.00", className: "mt-1.5 font-semibold", "data-testid": "invoice-amount-input" })
          )
        )
      )),

      // Section: Payment Type
      h(Card, { className: "border-slate-200" }, h(CardContent, { className: "p-5" },
        sectionHead(Wallet, "Payment Recommendation", "Full or partial payment against this PO"),
        h("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" },
          [
            { v: "FULL", label: "Full Payment", desc: "Release full balance amount", icon: CheckCircle2,
              activeClass: "border-emerald-400 bg-emerald-50 shadow-sm",
              iconWrapClass: "bg-emerald-100 text-emerald-700" },
            { v: "PART", label: "Part Payment", desc: "Release a percentage of PO value", icon: Clock,
              activeClass: "border-amber-400 bg-amber-50 shadow-sm",
              iconWrapClass: "bg-amber-100 text-amber-700" },
          ].map(opt => h("button", {
            key: opt.v, type: "button", onClick: () => set("payment_type", opt.v),
            className: "p-4 rounded-xl border-2 text-left transition-all " +
              (form.payment_type === opt.v ? opt.activeClass : "border-slate-200 hover:border-slate-300 bg-white"),
            "data-testid": "payment-type-" + opt.v.toLowerCase()
          },
            h("div", { className: "flex items-start gap-3" },
              h("div", { className: "p-2 rounded-md " + opt.iconWrapClass }, h(opt.icon, { className: "w-4 h-4" })),
              h("div", null,
                h("p", { className: "font-semibold text-sm text-slate-900" }, opt.label),
                h("p", { className: "text-xs text-slate-500 mt-0.5" }, opt.desc)
              )
            )
          ))
        ),
        form.payment_type === "PART" && h("div", { className: "p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2" },
          h(Label, { className: "text-xs font-semibold text-amber-900" }, "Part Payment Percentage (1\u201399%) *"),
          h("div", { className: "flex items-center gap-2" },
            h(Input, { type: "number", min: 1, max: 99, value: form.part_payment_percentage, onChange: e => set("part_payment_percentage", e.target.value), className: "h-9 w-32", placeholder: "e.g. 30" }),
            h("span", { className: "text-sm text-amber-900 font-semibold" }, "%"),
            form.part_payment_percentage && h("span", { className: "text-xs text-amber-700 ml-3" },
              "= ", fmtINR(suggestedAmount), " of PO value"
            )
          )
        ),
        // Suggested amount banner
        h("div", { className: "mt-4 p-4 rounded-lg bg-slate-900 text-white flex items-center justify-between" },
          h("div", null,
            h("p", { className: "text-xs uppercase tracking-wider text-slate-300 font-semibold" }, "Suggested Amount"),
            h("p", { className: "text-xs text-slate-400 mt-0.5" },
              form.payment_type === "FULL" ? "Balance after prior approved payments" : "Computed from PO value"
            )
          ),
          h("div", { className: "text-right" },
            h("p", { className: "text-2xl font-bold text-emerald-400" }, fmtINR(suggestedAmount)),
            !form.invoice_amount && suggestedAmount > 0 && h("button", {
              type: "button",
              onClick: () => set("invoice_amount", String(suggestedAmount)),
              className: "text-[11px] text-blue-300 hover:text-blue-100 underline mt-0.5"
            }, "Use as invoice amount")
          )
        )
      )),

      // Section: Remarks
      h(Card, { className: "border-slate-200" }, h(CardContent, { className: "p-5" },
        sectionHead(Info, "Remarks", "Optional notes for the approving authority"),
        h(Textarea, {
          value: form.remarks, onChange: e => set("remarks", e.target.value),
          rows: 3, className: "mt-2",
          placeholder: "Any additional context for F&A / approver \u2014 urgency, project relevance, dependencies..."
        })
      )),

      // Footer actions
      h("div", { className: "flex items-center justify-between sticky bottom-4 bg-white p-4 rounded-xl shadow-lg border border-slate-200" },
        h("div", { className: "text-sm text-slate-600" },
          h("p", { className: "font-semibold text-slate-900" }, "Ready to submit?"),
          h("p", { className: "text-xs text-slate-500" }, "Submission will route to S&P/F&A for approval.")
        ),
        h("div", { className: "flex gap-3" },
          h(Button, { type: "button", variant: "outline", onClick: () => navigate("/payments"), "data-testid": "cancel-btn" }, "Cancel"),
          h(Button, { type: "submit", disabled: submitting, className: "bg-slate-900 hover:bg-slate-800", "data-testid": "submit-btn" },
            h(Save, { className: "w-4 h-4 mr-2" }), submitting ? "Submitting..." : "Submit Payment Advice"
          )
        )
      )
    )
  );
}

export default PaymentRecommendationForm;
